/*
 * Client-side résumé privacy sanitizer.
 *
 * The résumé file is opened, parsed, sanitized, and reduced to a plain-text
 * "cleaned" version entirely inside the user's browser. The original file
 * bytes and the raw extracted text are NEVER transmitted to the backend,
 * analytics, logs, or any external service. Only the sanitized text may
 * leave the browser (for interview generation).
 *
 * Supported inputs: .pdf (text-readable) and .docx only.
 * Rejects: .doc, images, scanned/image-only PDFs, password-protected PDFs,
 *          corrupted files, oversized files.
 */

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import mammoth from "mammoth/mammoth.browser.js";
import { scrubText } from "./resumeScrubber";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.mjs";

export { scrubText };

export const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const SANITIZE_STAGES = {
  READING: "reading",
  ANALYZING: "analyzing",
  SCRUBBING: "scrubbing",
  DONE: "done",
};

export const SANITIZE_ERROR_CODES = {
  UNSUPPORTED_TYPE: "unsupported_type",
  LEGACY_DOC: "legacy_doc",
  FILE_TOO_LARGE: "file_too_large",
  PDF_PASSWORD: "pdf_password",
  PDF_CORRUPT: "pdf_corrupt",
  DOCX_CORRUPT: "docx_corrupt",
  NO_TEXT: "no_text",
  TOO_SHORT: "too_short",
};

export class SanitizerError extends Error {
  constructor(code, message) {
    super(message || code);
    this.code = code;
  }
}

const DOCX_EXT = ".docx";
const PDF_EXT = ".pdf";
const DOC_EXT = ".doc";

export function validateFile(file) {
  if (!file || !file.name) {
    throw new SanitizerError(SANITIZE_ERROR_CODES.UNSUPPORTED_TYPE);
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new SanitizerError(SANITIZE_ERROR_CODES.FILE_TOO_LARGE);
  }
  const name = file.name.toLowerCase();
  if (name.endsWith(DOC_EXT) && !name.endsWith(DOCX_EXT)) {
    throw new SanitizerError(SANITIZE_ERROR_CODES.LEGACY_DOC);
  }
  if (!name.endsWith(PDF_EXT) && !name.endsWith(DOCX_EXT)) {
    throw new SanitizerError(SANITIZE_ERROR_CODES.UNSUPPORTED_TYPE);
  }
  return name.endsWith(PDF_EXT) ? "pdf" : "docx";
}

async function extractPdfText(file) {
  let arrayBuffer = null;
  let pdf = null;
  const pages = [];

  try {
    arrayBuffer = await file.arrayBuffer();
    try {
      pdf = await pdfjsLib.getDocument({ data: arrayBuffer, password: "" }).promise;
    } catch (e) {
      if (e && (e.name === "PasswordException" || /password/i.test(String(e.message || e)))) {
        throw new SanitizerError(SANITIZE_ERROR_CODES.PDF_PASSWORD);
      }
      throw new SanitizerError(SANITIZE_ERROR_CODES.PDF_CORRUPT);
    }

    for (let i = 1; i <= pdf.numPages; i++) {
      let page = null;
      let content = null;
      try {
        page = await pdf.getPage(i);
        content = await page.getTextContent();
        let lastY = null;
        const line = [];
        const pageLines = [];
        for (const item of content.items) {
          const y = item.transform ? item.transform[5] : 0;
          if (lastY !== null && Math.abs(y - lastY) > 2) {
            pageLines.push(line.join(" "));
            line.length = 0;
          }
          line.push(item.str || "");
          lastY = y;
        }
        if (line.length) pageLines.push(line.join(" "));
        pages.push(pageLines.join("\n"));
      } finally {
        if (page && typeof page.cleanup === "function") {
          try { page.cleanup(); } catch { /* best-effort cleanup */ }
        }
        content = null;
        page = null;
      }
    }

    return pages.join("\n\n");
  } catch (e) {
    if (e instanceof SanitizerError) throw e;
    throw new SanitizerError(SANITIZE_ERROR_CODES.PDF_CORRUPT);
  } finally {
    if (pdf) {
      try {
        if (typeof pdf.cleanup === "function") await pdf.cleanup();
      } catch { /* best-effort cleanup */ }
      try {
        if (typeof pdf.destroy === "function") await pdf.destroy();
      } catch { /* best-effort cleanup */ }
    }
    pages.length = 0;
    pdf = null;
    arrayBuffer = null;
  }
}

async function extractDocxText(file) {
  let arrayBuffer = null;
  let result = null;
  let html = "";

  try {
    arrayBuffer = await file.arrayBuffer();
    result = await mammoth.convertToHtml({ arrayBuffer });
    html = result.value || "";
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|li|tr|h[1-6]|div|section)>/gi, "\n")
      .replace(/<(td|th)[^>]*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  } catch {
    throw new SanitizerError(SANITIZE_ERROR_CODES.DOCX_CORRUPT);
  } finally {
    html = "";
    result = null;
    arrayBuffer = null;
  }
}

/*
 * Plain text of a job description file (.pdf, .docx or .txt) for the admin
 * bulk question audit. No privacy scrub: on the upload page a job description
 * is pasted as text and sent as-is, so the batch sends it the same way.
 */
export async function extractDocumentText(file) {
  if (!file || !file.name) throw new SanitizerError(SANITIZE_ERROR_CODES.UNSUPPORTED_TYPE);
  if (file.size > MAX_FILE_BYTES) throw new SanitizerError(SANITIZE_ERROR_CODES.FILE_TOO_LARGE);
  const name = file.name.toLowerCase();
  let text = "";
  if (name.endsWith(".txt")) {
    text = await file.text();
  } else {
    const kind = validateFile(file);
    text = kind === "pdf" ? await extractPdfText(file) : await extractDocxText(file);
  }
  text = (text || "").replace(/\r\n?/g, "\n").trim();
  if (!text) throw new SanitizerError(SANITIZE_ERROR_CODES.NO_TEXT);
  return text;
}

export async function sanitizeResume(file, { onStage } = {}) {
  const emit = (s) => { try { onStage && onStage(s); } catch { /* noop */ } };
  const kind = validateFile(file);
  let raw = "";
  let trimmedRaw = "";
  let cleaned = "";
  let originalChars = 0;

  try {
    emit(SANITIZE_STAGES.READING);
    raw = kind === "pdf" ? await extractPdfText(file) : await extractDocxText(file);

    trimmedRaw = (raw || "").trim();
    raw = "";

    if (!trimmedRaw) throw new SanitizerError(SANITIZE_ERROR_CODES.NO_TEXT);
    if (kind === "pdf" && trimmedRaw.length < 120) {
      throw new SanitizerError(SANITIZE_ERROR_CODES.NO_TEXT);
    }

    originalChars = trimmedRaw.length;
    emit(SANITIZE_STAGES.ANALYZING);
    await new Promise((resolve) => setTimeout(resolve, 0));

    emit(SANITIZE_STAGES.SCRUBBING);
    cleaned = scrubText(trimmedRaw);
    trimmedRaw = "";

    if (cleaned.replace(/\s+/g, " ").trim().length < 200) {
      throw new SanitizerError(SANITIZE_ERROR_CODES.TOO_SHORT);
    }

    emit(SANITIZE_STAGES.DONE);
    return {
      text: cleaned,
      warnings: [],
      meta: {
        kind,
        originalChars,
        cleanedChars: cleaned.length,
      },
    };
  } finally {
    raw = "";
    trimmedRaw = "";
    // Do not clear `cleaned` before return; it is the approved downstream value.
    // All pre-sanitization references owned by this function are released here.
  }
}
